/**
 * Step 1: Identidad del Cliente
 * Versión refactorizada con TanStack Form
 */
import { useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { AlertTriangle, User, Building2, Users, FileText } from 'lucide-react'
import { cn, capitalizarNombre } from '@/lib/utils'
import { FormCUIT } from '../components/FormCUIT'
import { getFieldError, type ClienteForm } from '../useClienteForm'
import { useConsultarCuitAFIP } from '@/hooks/useConsultarCuitAFIP'
import { normalizarCategoriaMonotributo } from '@/lib/monotributo'
import type { ConsultaPadronResponse, PersonaAFIP } from '@/types/afip'

interface StepIdentidadProps {
  form: ClienteForm
  /** ID del cliente actual (para excluir en verificación de unicidad de CUIT en modo edición) */
  clienteId?: string
}

// Helper para detectar tipo de persona según CUIT
function getTipoPersonaFromCuit(cuit: string | null): 'fisica' | 'juridica' | null {
  if (!cuit) return null
  const str = cuit.toString().replace(/\D/g, '').padStart(11, '0')
  if (str.length !== 11) return null
  const prefijo = str.slice(0, 2)
  if (['20', '23', '24', '27'].includes(prefijo)) return 'fisica'
  if (['30', '33', '34'].includes(prefijo)) return 'juridica'
  return null
}

// Map AFIP condicion IVA values to form values
const CONDICION_IVA_MAP: Record<string, string> = {
  responsable_inscripto: 'responsable_inscripto',
  monotributista: 'monotributo',
  exento: 'exento',
}

const TIPO_PERSONA_OPTIONS = [
  {
    value: 'fisica' as const,
    label: 'Persona Física',
    description: 'Persona humana individual',
    icon: User,
  },
  {
    value: 'sucesion_indivisa' as const,
    label: 'Sucesión Indivisa',
    description: 'Herencia no dividida',
    icon: Users,
  },
  {
    value: 'juridica' as const,
    label: 'Sociedad',
    description: 'Empresa o persona jurídica',
    icon: Building2,
  },
]

export function StepIdentidadNew({ form, clienteId }: StepIdentidadProps) {

  // Auto-complete form fields from ARCA data (A5)
  const autoCompletarDesdeARCA = useCallback(
    (persona: PersonaAFIP) => {
      // Tipo persona
      if (persona.tipoPersona === 'FISICA') {
        form.setFieldValue('tipo_persona', 'fisica')
        if (persona.nombre) form.setFieldValue('nombre', persona.nombre)
        if (persona.apellido) form.setFieldValue('apellido', persona.apellido)
      } else if (persona.tipoPersona === 'JURIDICA') {
        form.setFieldValue('tipo_persona', 'juridica')
        if (persona.razonSocial) form.setFieldValue('nombre', persona.razonSocial)
        form.setFieldValue('apellido', '')
      }

      // Domicilio fiscal (full address string)
      if (persona.domicilio.provincia) {
        form.setFieldValue('domicilio_provincia', persona.domicilio.provincia)
      }
      if (persona.domicilio.localidad) {
        form.setFieldValue('domicilio_localidad', persona.domicilio.localidad)
      }
      if (persona.domicilio.codigoPostal) {
        form.setFieldValue('domicilio_cp', persona.domicilio.codigoPostal)
      }

      // Domicilio parsed (structured components from address string)
      if (persona.domicilioParsed?.calle) {
        form.setFieldValue('domicilio_calle', persona.domicilioParsed.calle)
      }
      if (persona.domicilioParsed?.numero) {
        form.setFieldValue('domicilio_numero', persona.domicilioParsed.numero)
      }
      if (persona.domicilioParsed?.piso) {
        form.setFieldValue('domicilio_piso', persona.domicilioParsed.piso)
      }
      if (persona.domicilioParsed?.depto) {
        form.setFieldValue('domicilio_depto', persona.domicilioParsed.depto)
      }

      // Fiscal
      if (persona.condicionIVA) {
        const mapped = CONDICION_IVA_MAP[persona.condicionIVA]
        if (mapped) form.setFieldValue('condicion_iva', mapped)
      }
      // ORR-233: normalización compartida con la re-lectura del padrón. El
      // `charAt(0)` que había acá devolvía "C" para "Categoría D" — una categoría
      // válida y equivocada, que entraba en el alta sin que nada fallara.
      const categoria = normalizarCategoriaMonotributo(persona.categoriaMonotributo)
      if (categoria) {
        form.setFieldValue('categoria_monotributo', categoria)
      }
      if (persona.actividadPrincipal) {
        form.setFieldValue('actividad', persona.actividadPrincipal)
      }
      if (persona.codigoActividadPrincipal) {
        form.setFieldValue('codigo_actividad_afip', persona.codigoActividadPrincipal)
      }
      if (persona.actividades.length > 0) {
        form.setFieldValue(
          'actividades_economicas',
          persona.actividades.map((a, i) => ({
            codigo: a.codigo,
            descripcion: a.descripcion,
            es_principal: i === 0,
          }))
        )
      }

      // Regimen IIBB (deduced from impuestos inscriptos)
      if (persona.impuestosInscriptos?.length > 0) {
        const tieneConvenio = persona.impuestosInscriptos.some(
          (i) => i.id === '5900' && i.estado === 'ACTIVO'
        )
        const tieneIIBBLocal = persona.impuestosInscriptos.some((i) => {
          const id = parseInt(i.id, 10)
          return id >= 5001 && id <= 5899 && i.estado === 'ACTIVO'
        })
        if (tieneConvenio) {
          form.setFieldValue('regimen_iibb', 'convenio_multilateral')
        } else if (tieneIIBBLocal) {
          form.setFieldValue('regimen_iibb', 'local')
        }
      }
    },
    [form]
  )

  const handleARCASuccess = useCallback(
    (data: ConsultaPadronResponse) => {
      if (data.encontrado && data.persona) {
        autoCompletarDesdeARCA(data.persona)
      }
    },
    [autoCompletarDesdeARCA]
  )

  const { consultarCuit, data: arcaData, isLoading: isLoadingARCA } = useConsultarCuitAFIP({
    onSuccess: handleARCASuccess,
  })

  return (
    <div className="space-y-6">
      {/* Tipo de Persona - Cards visuales */}
      <form.Field name="tipo_persona">
        {(field) => (
          <div className="space-y-3">
            <Label className="text-base font-semibold">Tipo de Persona *</Label>
            <RadioGroup
              value={field.state.value}
              onValueChange={(value) => {
                field.handleChange(value as 'fisica' | 'juridica' | 'sucesion_indivisa')
                // Limpiar campos que no aplican
                if (value === 'fisica') {
                  // Persona física no tiene admin/representante
                  form.setFieldValue('admin_cuit', null)
                  form.setFieldValue('admin_nombre', null)
                }
                if (value === 'juridica') {
                  form.setFieldValue('apellido', '')
                }
              }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3"
            >
              {TIPO_PERSONA_OPTIONS.map((option) => {
                const Icon = option.icon
                const isSelected = field.state.value === option.value
                return (
                  <Label
                    key={option.value}
                    htmlFor={`tipo-${option.value}`}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-xl border-2 p-4 cursor-pointer transition-all',
                      isSelected
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50'
                    )}
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={`tipo-${option.value}`}
                      className="sr-only"
                    />
                    <Icon
                      className={cn(
                        'h-6 w-6 transition-colors',
                        isSelected ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                    <span className={cn('font-medium text-sm text-center', isSelected && 'text-primary')}>
                      {option.label}
                    </span>
                    <span className="text-xs text-muted-foreground text-center hidden sm:block">
                      {option.description}
                    </span>
                  </Label>
                )
              })}
            </RadioGroup>
          </div>
        )}
      </form.Field>

      {/* Nombre/Razón Social */}
      <form.Field name="tipo_persona">
        {(tipoField) => (
          <form.Field name="nombre">
            {(field) => {
              const isJuridica = tipoField.state.value === 'juridica'
              const isSucesion = tipoField.state.value === 'sucesion_indivisa'
              const hasError = field.state.meta.errors.length > 0

              return isJuridica ? (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="nombre" className="text-base font-semibold">
                      Razón Social *
                    </Label>
                    <Input
                      id="nombre"
                      placeholder="Ej: Empresa SRL"
                      value={field.state.value}
                      onChange={(e) => {
                        const valorCapitalizado = capitalizarNombre(e.target.value)
                        field.handleChange(valorCapitalizado)
                      }}
                      onBlur={field.handleBlur}
                      className={cn('h-11', hasError && 'border-destructive focus-visible:ring-destructive')}
                    />
                    <p className={cn('text-sm h-5', hasError ? 'text-destructive' : 'text-transparent')}>
                      {getFieldError(field.state.meta.errors) || '\u00A0'}
                    </p>
                  </div>

                  {/* Nombre de Fantasía - Solo para Persona Jurídica */}
                  <form.Field name="nombre_fantasia">
                    {(fantasiaField) => (
                      <div className="space-y-1">
                        <Label htmlFor="nombre_fantasia">
                          Nombre de Fantasía
                          <span className="text-muted-foreground text-xs ml-1">(Opcional)</span>
                        </Label>
                        <Input
                          id="nombre_fantasia"
                          placeholder="Ej: Estudio ABC"
                          value={fantasiaField.state.value || ''}
                          onChange={(e) => {
                            const valorCapitalizado = capitalizarNombre(e.target.value)
                            fantasiaField.handleChange(valorCapitalizado)
                          }}
                          onBlur={fantasiaField.handleBlur}
                          className="h-11"
                        />
                        <p className="text-sm h-5 text-transparent">{'\u00A0'}</p>
                      </div>
                    )}
                  </form.Field>
                </>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="nombre" className="text-base font-semibold">
                      {isSucesion ? 'Nombre del Causante *' : 'Nombre *'}
                    </Label>
                    <Input
                      id="nombre"
                      placeholder="Ej: Juan"
                      value={field.state.value}
                      onChange={(e) => {
                        const valorCapitalizado = capitalizarNombre(e.target.value)
                        field.handleChange(valorCapitalizado)
                      }}
                      onBlur={field.handleBlur}
                      className={cn('h-11', hasError && 'border-destructive focus-visible:ring-destructive')}
                    />
                    <p className={cn('text-sm h-5', hasError ? 'text-destructive' : 'text-transparent')}>
                      {getFieldError(field.state.meta.errors) || '\u00A0'}
                    </p>
                  </div>
                  <form.Field name="apellido">
                    {(apellidoField) => {
                      const apellidoError = apellidoField.state.meta.errors.length > 0
                      return (
                        <div className="space-y-1">
                          <Label htmlFor="apellido" className="text-base font-semibold">
                            {isSucesion ? 'Apellido del Causante *' : 'Apellido *'}
                          </Label>
                          <Input
                            id="apellido"
                            placeholder="Ej: Pérez"
                            value={apellidoField.state.value || ''}
                            onChange={(e) => {
                              const valorCapitalizado = capitalizarNombre(e.target.value)
                              apellidoField.handleChange(valorCapitalizado)
                            }}
                            onBlur={apellidoField.handleBlur}
                            className={cn('h-11', apellidoError && 'border-destructive focus-visible:ring-destructive')}
                          />
                          <p className={cn('text-sm h-5', apellidoError ? 'text-destructive' : 'text-transparent')}>
                            {getFieldError(apellidoField.state.meta.errors) || '\u00A0'}
                          </p>
                        </div>
                      )
                    }}
                  </form.Field>
                </div>
              )
            }}
          </form.Field>
        )}
      </form.Field>

      {/* CUIT */}
      <FormCUIT
        form={form}
        name="cuit"
        label="CUIT"
        required
        excluirClienteId={clienteId}
        onConsultarARCA={(cuit) => consultarCuit(cuit)}
        isConsultandoARCA={isLoadingARCA}
        datosARCA={arcaData?.persona ?? null}
      />

      {/* Warning si CUIT no coincide con tipo persona */}
      <form.Field name="cuit">
        {(cuitField) => (
          <form.Field name="tipo_persona">
            {(tipoField) => {
              const tipoSegunCuit = getTipoPersonaFromCuit(cuitField.state.value)
              const tipoActual = tipoField.state.value
              const showWarning =
                tipoSegunCuit &&
                tipoSegunCuit !== tipoActual &&
                tipoActual !== 'sucesion_indivisa' &&
                cuitField.state.value?.replace(/\D/g, '').length === 11

              if (!showWarning) return null

              const tipoEsperado = tipoSegunCuit === 'fisica' ? 'Persona Física' : 'Persona Jurídica'
              const tipoSeleccionado = tipoActual === 'fisica' ? 'Persona Física' : 'Persona Jurídica'

              return (
                <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300 -mt-4">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p className="text-xs">
                    El CUIT corresponde a <strong>{tipoEsperado}</strong> pero se seleccionó{' '}
                    <strong>{tipoSeleccionado}</strong>.
                  </p>
                </div>
              )
            }}
          </form.Field>
        )}
      </form.Field>

      {/* Warning si CUIT está inactivo en ARCA */}
      {arcaData?.persona?.estadoClave === 'INACTIVO' && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-300 -mt-4">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-xs">
            Este CUIT figura como <strong>INACTIVO</strong> en ARCA/AFIP. Verificar la situación fiscal del contribuyente.
          </p>
        </div>
      )}

      {/* Email + Teléfono */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.Field name="email">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0
            return (
              <div className="space-y-1 min-w-0">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className={cn('h-11', hasError && 'border-destructive focus-visible:ring-destructive')}
                />
                <p className={cn('text-sm h-5', hasError ? 'text-destructive' : 'text-transparent')}>
                  {getFieldError(field.state.meta.errors) || '\u00A0'}
                </p>
              </div>
            )
          }}
        </form.Field>

        <form.Field name="telefono">
          {(field) => (
            <div className="space-y-1 min-w-0">
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                placeholder="+54 9 11 1234-5678"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
              <p className="text-sm h-5 text-transparent">{'\u00A0'}</p>
            </div>
          )}
        </form.Field>
      </div>

      {/* Administrador/Representante Legal - para Sucesión Indivisa y Sociedad */}
      <form.Field name="tipo_persona">
        {(tipoField) => {
          const tipo = tipoField.state.value
          const isSucesion = tipo === 'sucesion_indivisa'
          const isJuridica = tipo === 'juridica'

          if (!isSucesion && !isJuridica) return null

          // Labels dinámicos según tipo
          const titulo = isSucesion ? 'Datos del Administrador' : 'Datos del Representante Legal'
          const descripcion = isSucesion
            ? 'Persona que administra la sucesión hasta su finalización.'
            : 'Persona autorizada a representar legalmente a la sociedad (opcional).'
          const labelNombre = isSucesion ? 'Nombre del Administrador' : 'Nombre del Representante'
          const labelCuit = isSucesion ? 'CUIT del Administrador' : 'CUIT del Representante'
          const esRequerido = isSucesion // Solo requerido para sucesión

          return (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <div>
                <h4 className="text-sm font-medium">{titulo}</h4>
                <p className="text-xs text-muted-foreground">
                  {descripcion}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <form.Field name="admin_nombre">
                  {(field) => {
                    const hasError = field.state.meta.errors.length > 0
                    return (
                      <div className="space-y-1">
                        <Label htmlFor="admin_nombre">{labelNombre}{esRequerido ? ' *' : ''}</Label>
                        <Input
                          id="admin_nombre"
                          placeholder="Ej: María García"
                          value={field.state.value || ''}
                          onChange={(e) => {
                            const valorCapitalizado = capitalizarNombre(e.target.value)
                            field.handleChange(valorCapitalizado)
                          }}
                          onBlur={field.handleBlur}
                          className={cn('h-11', hasError && 'border-destructive focus-visible:ring-destructive')}
                        />
                        <p className={cn('text-sm h-5', hasError ? 'text-destructive' : 'text-transparent')}>
                          {getFieldError(field.state.meta.errors) || '\u00A0'}
                        </p>
                      </div>
                    )
                  }}
                </form.Field>
                <FormCUIT form={form} name="admin_cuit" label={labelCuit} required={esRequerido} verificarUnicidad={false} />
              </div>
            </div>
          )
        }}
      </form.Field>

      {/* Datos de Inscripción - Solo para Sociedad */}
      <form.Field name="tipo_persona">
        {(tipoField) => {
          if (tipoField.state.value !== 'juridica') return null

          return (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <h4 className="text-sm font-medium">Datos de Inscripción</h4>
                  <span className="text-xs text-muted-foreground">(Opcional)</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <form.Field name="inscripcion_numero">
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="inscripcion_numero">Número</Label>
                      <Input
                        id="inscripcion_numero"
                        placeholder="Ej: 12345"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        className="h-11"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="inscripcion_fojas">
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="inscripcion_fojas">Fojas</Label>
                      <Input
                        id="inscripcion_fojas"
                        placeholder="Ej: 456"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        className="h-11"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="inscripcion_tomo">
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="inscripcion_tomo">Tomo</Label>
                      <Input
                        id="inscripcion_tomo"
                        placeholder="Ej: A"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        className="h-11"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="inscripcion_protocolo">
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="inscripcion_protocolo">Protocolo</Label>
                      <Input
                        id="inscripcion_protocolo"
                        placeholder="Ej: 123/2020"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        className="h-11"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="inscripcion_fecha">
                  {(field) => {
                    const hasError = field.state.meta.errors.length > 0
                    const today = new Date().toISOString().split('T')[0]
                    return (
                      <div className="space-y-1">
                        <Label htmlFor="inscripcion_fecha">Fecha de Inscripción</Label>
                        <Input
                          id="inscripcion_fecha"
                          type="date"
                          max={today}
                          value={field.state.value || ''}
                          onChange={(e) => field.handleChange(e.target.value || null)}
                          className={cn('h-11', hasError && 'border-destructive')}
                        />
                        {hasError && (
                          <p className="text-sm text-destructive">
                            {getFieldError(field.state.meta.errors)}
                          </p>
                        )}
                      </div>
                    )
                  }}
                </form.Field>
                <form.Field name="inscripcion_ano">
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="inscripcion_ano">Año</Label>
                      <Input
                        id="inscripcion_ano"
                        type="number"
                        placeholder="Ej: 2020"
                        min={1900}
                        max={new Date().getFullYear()}
                        value={field.state.value || ''}
                        onChange={(e) => {
                          const val = e.target.value
                          field.handleChange(val ? parseInt(val, 10) : null)
                        }}
                        className="h-11"
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            </div>
          )
        }}
      </form.Field>
    </div>
  )
}
